import { useEffect, useState } from 'react'
import { BellOff } from 'lucide-react'
import type { Session } from '@supabase/supabase-js'
import { alertsApi, filtersApi, type Alert, type FilterOptions } from '../services/api'
import { reportError } from '../utils/errors'
import AlertCard from '../components/AlertCard'
import AlertForm from '../components/AlertForm'
import ConfirmDialog from '../components/ConfirmDialog'
import Modal from '../components/Modal'
import PlanLimitModal from '../components/PlanLimitModal'
import PostingsModal from '../components/PostingsModal'

interface Props {
  session: Session
  deepLinkAlertId?: string
  deepLinkPostingId?: string
}


export default function AlertsPage({ session, deepLinkAlertId, deepLinkPostingId }: Props) {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [alertsLimit, setAlertsLimit] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null)
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null)
  const [confirmDeletionId, setConfirmDeletionId] = useState<string | null>(null)
  const [createError, setCreateError] = useState('')
  const [backfillAlert, setBackfillAlert] = useState(false)
  const [filterOptions, setFilterOptions] = useState<FilterOptions | undefined>(undefined)
  const [confirmingCloseNew, setConfirmingCloseNew] = useState(false)
  const [confirmingCloseEdit, setConfirmingCloseEdit] = useState(false)

  async function load() {
    try {
      const [list, limitInfo, filters] = await Promise.all([
        alertsApi.list(session),
        alertsApi.getLimit(session),
        filtersApi.get(session),
      ])
      setAlerts(list)
      setAlertsLimit(limitInfo.limit)
      setFilterOptions(filters)
    } catch (e: unknown) {
      setError(reportError(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    if (deepLinkAlertId || deepLinkPostingId) {
      window.history.replaceState({}, '', window.location.pathname)
    }
    // Clear any saved redirect — the user is now on the app (logged-in path)
    sessionStorage.removeItem('postLoginRedirect')
  }, [])

  // Open deep-linked alert once alerts are loaded
  const deepLinkHandled = useState(false)
  useEffect(() => {
    if (loading || deepLinkHandled[0] || !deepLinkAlertId) return
    const target = alerts.find(a => a.id === deepLinkAlertId)
    if (target) {
      deepLinkHandled[1](true)
      setSelectedAlert(target)
    }
  }, [loading, alerts])

  async function handleCreate(name: string, filters: Record<string, unknown>): Promise<boolean> {
    try {
      const alert = await alertsApi.create(name, filters, session)
      setAlerts(prev => [...prev, alert])
      setCreateError('')
      setShowModal(false)
      setSelectedAlert(alert)
      setBackfillAlert(true)
      return true
    } catch (e: unknown) {
      const err = e as Error & { status?: number }
      if (err.status === 402) {
        setShowModal(false)
        setShowLimitModal(true)
      } else if (err.status === 409) {
        setCreateError(err.message)
      } else {
        setError(reportError(e))
      }
      return false
    }
  }

  async function handleDelete(id: string) {
    setConfirmDeletionId(null)
    await alertsApi.delete(id, session)
    setAlerts(prev => prev.filter(a => a.id !== id))
  }

  function handleCardClick(alert: Alert) {
    setConfirmDeletionId(null)
    setSelectedAlert(alert)
  }

  async function handleUpdate(name: string, filters: Record<string, unknown>): Promise<boolean> {
    if (!editingAlert) return false
    try {
      const updated = await alertsApi.update(editingAlert.id, name, filters, session)
      setAlerts(prev => prev.map(a => a.id === updated.id ? updated : a))
      setEditingAlert(null)
      return true
    } catch (e: unknown) {
      // Always reload from server so the UI reflects actual state,
      // regardless of whether the request timed out or failed mid-way.
      await load()
      setEditingAlert(null)
      setError(reportError(e))
      return false
    }
  }

  if (loading) return <p className="p-10 text-gray-500">Caricamento…</p>

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">I miei Avvisi</h1>
        <button
          onClick={() => alertsLimit !== null && alerts.length >= alertsLimit ? setShowLimitModal(true) : setShowModal(true)}
          className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-lg px-4 py-2 transition-colors"
        >
          + Nuovo Avviso
        </button>
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      {/* Alert list */}
      {alerts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <BellOff size={48} className="text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Nessun avviso ancora</h2>
          <p className="text-gray-500 text-sm mb-6 max-w-xs">
            Crea il tuo primo avviso per iniziare a monitorare i concorsi pubblici.
          </p>
          <button
            onClick={() => alertsLimit !== null && alerts.length >= alertsLimit ? setShowLimitModal(true) : setShowModal(true)}
            className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-lg px-5 py-2.5 transition-colors"
          >
            Crea avviso
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {alerts.map(a => (
            <AlertCard
              key={a.id}
              alert={a}
              isConfirming={confirmDeletionId === a.id}
              onRequestDelete={id => setConfirmDeletionId(id || null)}
              onDelete={handleDelete}
              onEdit={alert => { setConfirmDeletionId(null); setEditingAlert(alert) }}
              onClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* New alert modal */}
      {showModal && (
        <Modal title="Nuovo Avviso" onClose={() => setConfirmingCloseNew(true)}>
          <AlertForm
            onCreate={handleCreate}
            externalError={createError}
            filterOptions={filterOptions}
            session={session}
            onCancel={() => setConfirmingCloseNew(true)}
          />
        </Modal>
      )}
      {confirmingCloseNew && (
        <ConfirmDialog
          message="Sei sicuro di voler uscire dalla creazione dell'avviso? I progressi andranno persi."
          onConfirm={() => { setConfirmingCloseNew(false); setShowModal(false); setCreateError('') }}
          onCancel={() => setConfirmingCloseNew(false)}
        />
      )}

      {/* Edit alert modal */}
      {editingAlert && (
        <Modal title="Modifica Avviso" onClose={() => setConfirmingCloseEdit(true)}>
          <AlertForm
            onCreate={handleUpdate}
            initialValues={{ name: editingAlert.name, filters: editingAlert.filters }}
            filterOptions={filterOptions}
            session={session}
            onCancel={() => setConfirmingCloseEdit(true)}
          />
        </Modal>
      )}
      {confirmingCloseEdit && (
        <ConfirmDialog
          message="Sei sicuro di voler uscire? Le modifiche non salvate andranno perse."
          onConfirm={() => { setConfirmingCloseEdit(false); setEditingAlert(null) }}
          onCancel={() => setConfirmingCloseEdit(false)}
        />
      )}

      {/* Plan limit modal */}
      {showLimitModal && (
        <PlanLimitModal onClose={() => setShowLimitModal(false)} />
      )}

      {/* Postings modal */}
      {selectedAlert && (
        <PostingsModal
          alert={selectedAlert}
          session={session}
          backfill={backfillAlert}
          highlightPostingId={deepLinkPostingId}
          onClose={() => { setSelectedAlert(null); setBackfillAlert(false) }}
        />
      )}

    </div>
  )
}
